import React, { useState, useRef, useEffect } from 'react';
import JoditEditor from 'jodit-react';
import './ProfilePage.css';
import { FaBuilding, FaEdit, FaEnvelope, FaPhone, FaTimes } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { Base_Url } from '../config';
import axios from 'axios';
import { useSelector } from 'react-redux';
import dummy from "../Assests/dummy.jpg";

// Modal Component
const EditorModal = ({ isOpen, onClose, title, content, onSave }) => {
    const [editContent, setEditContent] = useState(content);
    const editor = useRef(null);

    const editorConfig = {
        readonly: false,
        height: "90vh",
        minHeight: 1000,
        maxHeight: 3000,
        editHTMLDocumentMode: true,
        uploader: {
            url: `${Base_Url}/upload-image`,
            filesVariableName: () => "image",
            withCredentials: false,
            format: "json",
            method: "POST",
            prepareData: (formData) => {
                const file = formData.get("image");
                formData.set("image", file);
                return formData;
            },
            isSuccess: (resp) => {
                if (resp.imageUrl) {
                    const newContent = `${editContent}<img src="${resp.imageUrl}" alt="Uploaded image" />`;
                    setEditContent(newContent);
                    return true;
                }
                return false;
            },
            getMessage: (resp) => resp.msgs ? resp.msgs.join("\n") : 'An error occurred',
            process: (resp) => resp,
        },
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <div className="modal-header">
                    <h3>{title}</h3>
                    <FaTimes className="close-icon" onClick={onClose} />
                </div>
                <div className="modal-body">
                    <JoditEditor
                        ref={editor}
                        value={editContent}
                        config={editorConfig}
                        tabIndex={1}
                        onBlur={(newContent) => setEditContent(newContent)}
                    />
                </div>
                <div className="modal-footer">
                    <button onClick={() => onSave(editContent)}>Save</button>
                    <button onClick={onClose}>Cancel</button>
                </div>
            </div>
        </div>
    );
};

const ProfilePage = () => {
    const [bioContent, setBioContent] = useState('');
    const [hasProfile, setHasProfile] = useState(false);
    const [profileId, setProfileId] = useState(null);
    const [facultyData, setFacultyData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // States for each section's content
    const [cvContent, setCvContent] = useState('');
    const [teachingContent, setTeachingContent] = useState('');
    const [academicsContent, setAcademicsContent] = useState('');
    const [othersContent, setOthersContent] = useState('');

    // Modal state
    const [activeModal, setActiveModal] = useState(null);

    const { id: facultyId } = useParams();
    const userfromwebmaster = useSelector((state) => state.user);
    const facultstaff = useSelector((state) => state.facultyuser);

    useEffect(() => {
        fetchProfile();
    }, [facultyId]);

    const fetchProfile = async () => {
        try {
            const response = await fetch(`${Base_Url}/profile/${facultyId}`);
            if (response.ok) {
                const data = await response.json();
                setBioContent(data.bio || '');
                setCvContent(data.cv_and_specialization || '');
                setTeachingContent(data.teaching_and_research || '');
                setAcademicsContent(data.academics || '');
                setOthersContent(data.others || '');
                setProfileId(data.id);
                setHasProfile(true);
                setFacultyData(data);
            } else {
                setHasProfile(false);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            setError('Failed to fetch profile data');
        } finally {
            setIsLoading(false);
        }
    };

    const saveContent = async (content, section) => {
        try {
            const userId = userfromwebmaster?.userData?.userid || facultstaff?.facultyprofileData?.user_id || "";
            const url = hasProfile ? `${Base_Url}/profile/${profileId}` : `${Base_Url}/profile`;
            const method = hasProfile ? 'PUT' : 'POST';

            // Map modal sections to database fields
            const sectionMapping = {
                bio: 'bio',
                cv: 'cv_and_specialization',
                teaching: 'teaching_and_research',
                academics: 'academics',
                others: 'others'
            };

            const body = {
                faculty_id: facultyId,
                [sectionMapping[section]]: content,
                ...(hasProfile ? { updated_by: userId } : { created_by: userId }),
            };

            // Include faculty data only when creating/updating bio
            if (section === 'bio') {
                Object.assign(body, {
                    name: facultyData?.name,
                    designation: facultyData?.title,
                    research_areas: facultyData?.research_areas,
                    email: facultyData?.email,
                    office: facultyData?.office,
                    phone: facultyData?.phone,
                    webpage_url: facultyData?.webpage_url,
                    study: facultyData?.study,
                });
            }

            const response = await axios({
                method,
                url,
                data: body,
                headers: { 'Content-Type': 'application/json' },
            });

            if (response.status === 200) {
                setHasProfile(true);
                if (response.data.id) {
                    setProfileId(response.data.id);
                }
                fetchProfile(); // Refresh data after save
                setActiveModal(null);
            }
        } catch (error) {
            console.error('Error saving content:', error);
            setError('Failed to save content. Please try again.');
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="profile-container">
            <div className="profile-card">
                {facultyData && (
                    <>
                        <img
                            src={facultyData?.image_path != null && facultyData?.image_path !== "" ? `${Base_Url}/${facultyData.image_path}` : dummy}
                            alt={facultyData.name}
                            className="profile-image"
                        />
                        <h2 className="profile-name text-white">{facultyData.name}</h2>
                        <p className="profile-title">{facultyData.study}</p>
                        <div className="profile-contact">
                            <div>
                                <div className="d-flex">
                                    <FaEnvelope />
                                </div>
                                <p>
                                    <strong className="text-white">Email:</strong>
                                    <span className="w-75 text-white">{facultyData.email}</span>
                                </p>
                            </div>
                            <div>
                                <div className="d-flex">
                                    <FaPhone />
                                </div>
                                <p>
                                    <strong className="text-white">Phone:</strong>
                                    <span className="text-white">
                                        {facultyData.phone ? facultyData.phone : 'N/A'}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <div className="d-flex">
                                    <FaBuilding />
                                </div>
                                <p>
                                    <strong className="text-white">Office:</strong>
                                    <span className="text-white">{facultyData.office}</span>
                                </p>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="profile-details">
                {/* Bio Section */}
                <div className="bio-section">
                    <h3>
                        <span className="biotext ps-4 pe-4">Bio</span>
                    <FaEdit size={20} className="icon add-icon" onClick={() => setActiveModal('bio')} />
                    </h3>
                    <div dangerouslySetInnerHTML={{ __html: bioContent }} />
                </div>

                {/* CV Section */}
                <div className="bio-section">
                    <h3>
                        <span className="biotext ps-4 pe-4">CV And Specialization</span>
                    <FaEdit size={20} className="icon add-icon" onClick={() => setActiveModal('cv')} />
                    </h3>
                    <div dangerouslySetInnerHTML={{ __html: cvContent }} />
                </div>

                {/* Teaching Section */}
                <div className="bio-section">
                    <h3>
                        <span className="biotext ps-4 pe-4">Teaching And Research</span>
                    <FaEdit size={20} className="icon add-icon" onClick={() => setActiveModal('teaching')} />
                    </h3>
                    <div dangerouslySetInnerHTML={{ __html: teachingContent }} />
                </div>

                {/* Academics Section */}
                <div className="bio-section">
                    <h3>
                        <span className="biotext ps-4 pe-4">Academics</span>
                    <FaEdit size={20} className="icon add-icon" onClick={() => setActiveModal('academics')} />
                    </h3>
                    <div dangerouslySetInnerHTML={{ __html: academicsContent }} />
                </div>

                {/* Others Section */}
                <div className="bio-section">
                    <h3>
                        <span className="biotext ps-4 pe-4">Others</span>
                    <FaEdit size={20} className="icon add-icon" onClick={() => setActiveModal('others')} />
                    </h3>
                    <div dangerouslySetInnerHTML={{ __html: othersContent }} />
                </div>
            </div>

            {/* Modals for each section */}
            <EditorModal
                isOpen={activeModal === 'bio'}
                onClose={() => setActiveModal(null)}
                title="Bio"
                content={bioContent}
                onSave={(content) => saveContent(content, 'bio')}
            />

            <EditorModal
                isOpen={activeModal === 'cv'}
                onClose={() => setActiveModal(null)}
                title="CV And Specialization"
                content={cvContent}
                onSave={(content) => saveContent(content, 'cv')}
            />

            <EditorModal
                isOpen={activeModal === 'teaching'}
                onClose={() => setActiveModal(null)}
                title="Teaching And Research"
                content={teachingContent}
                onSave={(content) => saveContent(content, 'teaching')}
            />

            <EditorModal
                isOpen={activeModal === 'academics'}
                onClose={() => setActiveModal(null)}
                title="Academics"
                content={academicsContent}
                onSave={(content) => saveContent(content, 'academics')}
            />

            <EditorModal
                isOpen={activeModal === 'others'}
                onClose={() => setActiveModal(null)}
                title="Others"
                content={othersContent}
                onSave={(content) => saveContent(content, 'others')}
            />
        </div>
    );
};

export default ProfilePage;